/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect, useState } from "react";
import { DropdownToggle, DropdownMenu, DropdownItem, Dropdown, Navbar, Label, Tooltip } from "reactstrap";
import { initialContext, AccountContext, ContextUpdater } from "../context/ContextProvider";
import { requestAccount, requestAdminDashboardData, requestClientInfoAndTemplate } from "../context/RequestManager";
import { constants } from "../util/constants/Constants";
import SearchAnythingBar from "./SearchAnythingBar";
import ModalItemList from "../pages/admin/ModalItemList";
import packageJson from '../../package.json'

import smartWifiLogo from "../assets/images/smart_wifi_logo.png";
import smartWifiIcon from "../assets/images/smart_wifi_icon.png";
import { smartWiFiExternalBase } from "../util/constants/Endpoint";
import { customDomainLogo, getCustomDomainLogo, isCustomDomain } from "../util/CustomDomain";

const Header = () => {
  // context
  const updateContext = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);
  const clientName = accountContext.clientSummary?.name ?? accountContext.clientInfo?.name;

  // state
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const toggleUserDropdown = () => setUserDropdownOpen((prevState) => !prevState);

  const [logoState, setLogoState] = useState();

  const [tooltip, setTooltip] = useState(false);

  const [clientModalOpen, setClientModalOpen] = useState(false);
  const openClientModal = () => setClientModalOpen(true);
  const closeClientModal = () => setClientModalOpen(false);
  
  // variables
  const isAdmin = (accountContext.admin ?? false) || (accountContext.subAdmin ?? false);

  
  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    setLogoState(accountContext.clientInfo?.brandId ?? null);
  }, [accountContext.clientInfo]);

  // handlers
  const handleAccountSelected = () => {
    requestAccount({
      context: updateContext,
      token: accountContext.token,
      username: accountContext.username,
      navigate: (_) => "/dashboard-account"
    });
  };

  const handleLogout = () => {
    // clear contexts
    updateContext({ ...initialContext });

    // clear token from session storage
    sessionStorage.removeItem(constants.storageTokenKey);
  };

  const handleHelp = () => {
    const smartWiFiHelp = `${smartWiFiExternalBase}help.html`
    window.open(smartWiFiHelp, "_blank");
  }

  const handleSelectClient = (client) => {
    const { id, name } = client;

    // navigate back to admin dashboard
    requestClientInfoAndTemplate({
      context: updateContext,
      token: accountContext.token,
      clientId: id,
      navigate: () => "/dashboard",
      completion: (response) => {
        if (response.error) {
          return;
        }

        // set client selected
        accountContext.updateState({
          clientSummary: { id, name },
          clientId: id
        });
      }
    })
  };

  const handleUnselectClient = () => {
    // navigate back to admin dashboard
    requestAdminDashboardData({
      context: updateContext,
      token: accountContext.token,
      navigate: () => "/admin",
      completion: () => accountContext.updateState({
        clientSummary: null,
        clientInfo: null,
        clientId: null
      })
    });
  };

  // do not show if not logged in
  if (!accountContext.token) {
    return null;
  }
  
  return (
    <Navbar color="gray-500" style={{ padding: "0", flexWrap: 'nowrap' }}>
      {isCustomDomain() || logoState ? (
          <div className="flex-20 d-flex">
            <img src={logoState ? getCustomDomainLogo(logoState) : customDomainLogo() } alt="" style={{ height: "50px" }} />
          </div>
        ) : (
          <div className="flex-20 d-flex">
            <img src={smartWifiLogo} alt="" style={{ height: "36px" }} />
            <img src={smartWifiIcon} alt="" style={{ height: "36px" }} />
          </div>
        )
      }
      
      <div className="flex-60 d-flex">
        {isAdmin ? (
          <div className="pt-1 fill-container d-flex flex-column">
            {clientName ? (
              <div className="w-100 d-flex justify-content-center">
                <Label className="no-spacing text-center" tag="h5">
                  <span className="link-primary clickable" onClick={openClientModal}>Viewing Business:</span>
                  {' '}<span className="fw-bold">{clientName}</span>
                  <span id="unselect-business" className="ps-2 link-primary clickable" onClick={handleUnselectClient}>
                    <i className="bi-x-lg" />
                  </span>
                  <Tooltip
                    isOpen={tooltip}
                    target="unselect-business"
                    toggle={() => setTooltip(prev => !prev)}
                    placement="bottom"
                  >
                    Unselect Business
                  </Tooltip>
                </Label>
              </div>
            ) : (
              <Label className="no-spacing text-center link-primary clickable" tag="h5" onClick={openClientModal}>
                No Business Selected
              </Label>
            )}
            <SearchAnythingBar className="w-100" admin />  
          </div>
        ) : (
          <Label className="w-100 text-center" tag="h2">{clientName}</Label>
        )}
      </div>
      <div className="flex-20">
        <Dropdown className="fill-container d-flex justify-content-end" isOpen={userDropdownOpen} toggle={toggleUserDropdown}>
          <DropdownToggle color="transparent">
            <div className="d-flex align-items-center">
              <span>v{packageJson.version}</span>
              <i className="ps-2 icon bi bi-person-circle" style={{ color: "#32A852", fontSize: "2rem" }} />
            </div>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem header>{accountContext.username ?? (isAdmin ? "Admin" : "User")}</DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={handleAccountSelected}>Account</DropdownItem>
            <DropdownItem onClick={() => handleHelp()}>Help</DropdownItem>
            <DropdownItem onClick={() => handleLogout()}>Logout</DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
      {isAdmin && (
        <ModalItemList
          type="client"
          isOpen={clientModalOpen}
          items={accountContext.adminClientList}
          onClick={handleSelectClient}
          onDone={closeClientModal}
        />
      )}
    </Navbar>
  );
};

export default Header;